import { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// SNS Registry Contract
const SNS_REGISTRY_ADDRESS = '0x0b9766774C71983F5555FCc78269047FB926019E';
const RESOLVE_ADDRESS_SELECTOR = '0xf81e8775';

// Shido Mainnet RPC
const SHIDO_RPC = 'https://evm.shidoscan.net';

// Cache for resolved names to avoid repeated calls
const snsCache = new Map<string, string | null>();

/**
 * Resolve an address to its SNS (.shido) name
 */
export const resolveSNSName = async (address: string): Promise<string | null> => {
  if (!address || !ethers.utils.isAddress(address)) {
    return null;
  }

  const normalizedAddress = address.toLowerCase();
  
  // Check cache first
  if (snsCache.has(normalizedAddress)) {
    return snsCache.get(normalizedAddress) || null;
  }

  try {
    const provider = new ethers.providers.JsonRpcProvider(SHIDO_RPC);
    
    // Encode the function call: resolveAddress(address)
    // Function selector: 0xf81e8775
    // Pad the address to 32 bytes
    const paddedAddress = ethers.utils.hexZeroPad(address, 32);
    const callData = RESOLVE_ADDRESS_SELECTOR + paddedAddress.slice(2);
    
    const result = await provider.call({
      to: SNS_REGISTRY_ADDRESS,
      data: callData,
    });
    
    // Decode the result - it returns a string
    if (result && result !== '0x') {
      try {
        // The result is ABI encoded string
        const decoded = ethers.utils.defaultAbiCoder.decode(['string'], result);
        const snsName = decoded[0];
        
        if (snsName && snsName.length > 0) {
          // Cache the result
          snsCache.set(normalizedAddress, snsName);
          return snsName;
        }
      } catch (decodeError) {
        console.warn('Failed to decode SNS result:', decodeError);
      }
    }
    
    // Cache null result to avoid repeated failed lookups
    snsCache.set(normalizedAddress, null);
    return null;
  } catch (error) {
    console.error('Error resolving SNS name:', error);
    return null;
  }
};

/**
 * Hook to resolve SNS name for an address
 */
export const useSNS = (address: string | undefined) => {
  const [snsName, setSnsName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!address) {
      setSnsName(null);
      return;
    }

    let isMounted = true;
    
    const fetchSNS = async () => {
      setIsLoading(true);
      try {
        const name = await resolveSNSName(address);
        if (isMounted) {
          setSnsName(name);
        }
      } catch (error) {
        console.error('Error in useSNS:', error);
        if (isMounted) {
          setSnsName(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSNS();

    return () => {
      isMounted = false;
    };
  }, [address]);

  return { snsName, isLoading };
};

/**
 * Format address with SNS name for display
 * e.g., "0x1234...5678 (maverick.shido)"
 */
export const formatAddressWithSNS = (address: string, snsName: string | null): string => {
  const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
  if (snsName) {
    return `${shortAddress} (${snsName})`;
  }
  return shortAddress;
};
